import React, { useRef, useState, useCallback, useEffect } from 'react';

export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageAreaSelectorProps {
  value: Area | null;
  onChange: (area: Area | null) => void;
  onAngleChange?: (angle: number) => void;
  angle?: number;
  className?: string;
  style?: React.CSSProperties;
}

const HANDLE_SIZE = 8;
const MIN_SIZE = 10;
const ROTATION_HANDLE_SIZE = 28;

const ROTATION_HANDLE_OFFSET = 4;

function getRotationHandleRect(area: Area): { left: number; top: number; width: number; height: number } {
  return {
    left: area.x - ROTATION_HANDLE_SIZE - ROTATION_HANDLE_OFFSET,
    top: area.y + area.height + ROTATION_HANDLE_OFFSET,
    width: ROTATION_HANDLE_SIZE,
    height: ROTATION_HANDLE_SIZE,
  };
}

function hitRotationHandle(area: Area, px: number, py: number): boolean {
  const r = getRotationHandleRect(area);
  return px >= r.left && px <= r.left + r.width && py >= r.top && py <= r.top + r.height;
}

const DELETE_HANDLE_SIZE = 24;

function getDeleteHandleRect(area: Area): { left: number; top: number; width: number; height: number } {
  return {
    left: area.x + area.width + ROTATION_HANDLE_OFFSET,
    top: area.y - DELETE_HANDLE_SIZE - ROTATION_HANDLE_OFFSET,
    width: DELETE_HANDLE_SIZE,
    height: DELETE_HANDLE_SIZE,
  };
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

function getHandleRect(area: Area, handle: ResizeHandle): { left: number; top: number; width: number; height: number } {
  const half = HANDLE_SIZE / 2;
  const positions: Record<ResizeHandle, { x: number; y: number }> = {
    nw: { x: area.x, y: area.y },
    n: { x: area.x + area.width / 2, y: area.y },
    ne: { x: area.x + area.width, y: area.y },
    e: { x: area.x + area.width, y: area.y + area.height / 2 },
    se: { x: area.x + area.width, y: area.y + area.height },
    s: { x: area.x + area.width / 2, y: area.y + area.height },
    sw: { x: area.x, y: area.y + area.height },
    w: { x: area.x, y: area.y + area.height / 2 },
  };
  const { x, y } = positions[handle];
  return { left: x - half, top: y - half, width: HANDLE_SIZE, height: HANDLE_SIZE };
}

function hitHandle(area: Area, px: number, py: number): ResizeHandle | null {
  const handles: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  for (const h of handles) {
    const r = getHandleRect(area, h);
    if (px >= r.left && px <= r.left + r.width && py >= r.top && py <= r.top + r.height) return h;
  }
  return null;
}

function inArea(area: Area, px: number, py: number): boolean {
  return (
    px >= area.x && px <= area.x + area.width && py >= area.y && py <= area.y + area.height
  );
}

/** Преобразует точку из экранных координат в «неповёрнутые» относительно области с заданным углом */
function localToUnrotated(
  localX: number,
  localY: number,
  area: Area,
  angleDeg: number
): { x: number; y: number } {
  if (!angleDeg) return { x: localX, y: localY };
  const cx = area.x + area.width / 2;
  const cy = area.y + area.height / 2;
  const rad = (-angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = localX - cx;
  const dy = localY - cy;
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

export const ImageAreaSelector: React.FC<ImageAreaSelectorProps> = ({
  value,
  onChange,
  onAngleChange,
  angle = 0,
  className = '',
  style = {},
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'idle' | 'drawing' | 'moving' | 'resizing' | 'rotating'>('idle');
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);
  const [moveStart, setMoveStart] = useState<{ areaX: number; areaY: number; clientX: number; clientY: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ handle: ResizeHandle; area: Area; clientX: number; clientY: number } | null>(null);
  const [rotateStart, setRotateStart] = useState<{ centerX: number; centerY: number; startAngle: number; startDir: number } | null>(null);
  const [hoverHandle, setHoverHandle] = useState<ResizeHandle | null>(null);
  const [hoverInArea, setHoverInArea] = useState(false);
  const [hoverRotation, setHoverRotation] = useState(false);
  const onAngleChangeRef = useRef(onAngleChange);
  onAngleChangeRef.current = onAngleChange;

  const clientToLocal = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const el = overlayRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const local = clientToLocal(e.clientX, e.clientY);
      if (!local) return;

      if (!value) {
        setMode('drawing');
        setDrawStart({ x: local.x, y: local.y });
        setDrawCurrent({ x: local.x, y: local.y });
        return;
      }

      const angleNum = Number(angle) || 0;
      const pt = localToUnrotated(local.x, local.y, value, angleNum);

      const handle = hitHandle(value, pt.x, pt.y);
      if (handle) {
        setMode('resizing');
        setResizeStart({ handle, area: { ...value }, clientX: e.clientX, clientY: e.clientY });
        return;
      }

      if (onAngleChange && hitRotationHandle(value, pt.x, pt.y)) {
        e.stopPropagation();
        const centerX = value.x + value.width / 2;
        const centerY = value.y + value.height / 2;
        const startDir = Math.atan2(local.y - centerY, local.x - centerX);
        setMode('rotating');
        setRotateStart({
          centerX,
          centerY,
          startAngle: Number(angle) || 0,
          startDir,
        });
        return;
      }

      if (inArea(value, pt.x, pt.y)) {
        setMode('moving');
        setMoveStart({ areaX: value.x, areaY: value.y, clientX: e.clientX, clientY: e.clientY });
        return;
      }

      // Клик вне области — сбрасываем и начинаем новое выделение
      onChange(null);
      onAngleChange?.(0);
      setMode('drawing');
      setDrawStart({ x: local.x, y: local.y });
      setDrawCurrent({ x: local.x, y: local.y });
    },
    [value, angle, clientToLocal, onChange, onAngleChange]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const local = clientToLocal(e.clientX, e.clientY);
      if (!local) return;

      if (mode === 'idle' && value) {
        const angleNum = Number(angle) || 0;
        const pt = localToUnrotated(local.x, local.y, value, angleNum);
        const handle = hitHandle(value, pt.x, pt.y);
        const onRotation = hitRotationHandle(value, pt.x, pt.y);
        setHoverHandle(handle);
        setHoverRotation(!!onAngleChange && onRotation);
        setHoverInArea(handle || onRotation ? false : inArea(value, pt.x, pt.y));
      }

      if (mode === 'rotating' && rotateStart && onAngleChangeRef.current) {
        const currentDir = Math.atan2(local.y - rotateStart.centerY, local.x - rotateStart.centerX);
        const deltaDeg = ((currentDir - rotateStart.startDir) * 180) / Math.PI;
        const newAngle = rotateStart.startAngle + deltaDeg;
        onAngleChangeRef.current(Math.round(newAngle));
        return;
      }

      if (mode === 'drawing' && drawStart) {
        setDrawCurrent({ x: local.x, y: local.y });
        return;
      }

      if (mode === 'moving' && moveStart && value) {
        const rect = overlayRef.current?.getBoundingClientRect();
        if (!rect) return;
        const dx = e.clientX - moveStart.clientX;
        const dy = e.clientY - moveStart.clientY;
        let newX = moveStart.areaX + dx;
        let newY = moveStart.areaY + dy;
        newX = Math.max(0, Math.min(rect.width - value.width, newX));
        newY = Math.max(0, Math.min(rect.height - value.height, newY));
        onChange({ ...value, x: newX, y: newY });
        setMoveStart({ ...moveStart, areaX: newX, areaY: newY, clientX: e.clientX, clientY: e.clientY });
        return;
      }

      if (mode === 'resizing' && resizeStart && value) {
        const { handle, area } = resizeStart;
        const dx = e.clientX - resizeStart.clientX;
        const dy = e.clientY - resizeStart.clientY;
        const rect = overlayRef.current?.getBoundingClientRect();
        const maxW = rect ? rect.width : Infinity;
        const maxH = rect ? rect.height : Infinity;

        let x = area.x;
        let y = area.y;
        let width = area.width;
        let height = area.height;

        switch (handle) {
          case 'e':
            width = Math.max(MIN_SIZE, Math.min(area.width + dx, maxW - area.x));
            break;
          case 'w':
            width = Math.max(MIN_SIZE, area.width - dx);
            x = area.x + area.width - width;
            if (x < 0) { width = Math.max(MIN_SIZE, width + x); x = 0; }
            break;
          case 's':
            height = Math.max(MIN_SIZE, Math.min(area.height + dy, maxH - area.y));
            break;
          case 'n':
            height = Math.max(MIN_SIZE, area.height - dy);
            y = area.y + area.height - height;
            if (y < 0) { height = Math.max(MIN_SIZE, height + y); y = 0; }
            break;
          case 'se':
            width = Math.max(MIN_SIZE, Math.min(area.width + dx, maxW - area.x));
            height = Math.max(MIN_SIZE, Math.min(area.height + dy, maxH - area.y));
            break;
          case 'sw':
            width = Math.max(MIN_SIZE, area.width - dx);
            x = area.x + area.width - width;
            height = Math.max(MIN_SIZE, Math.min(area.height + dy, maxH - area.y));
            if (x < 0) { width = Math.max(MIN_SIZE, width + x); x = 0; }
            break;
          case 'ne':
            width = Math.max(MIN_SIZE, Math.min(area.width + dx, maxW - area.x));
            height = Math.max(MIN_SIZE, area.height - dy);
            y = area.y + area.height - height;
            if (y < 0) { height = Math.max(MIN_SIZE, height + y); y = 0; }
            break;
          case 'nw':
            width = Math.max(MIN_SIZE, area.width - dx);
            x = area.x + area.width - width;
            height = Math.max(MIN_SIZE, area.height - dy);
            y = area.y + area.height - height;
            if (x < 0) { width = Math.max(MIN_SIZE, width + x); x = 0; }
            if (y < 0) { height = Math.max(MIN_SIZE, height + y); y = 0; }
            break;
        }
        if (rect) {
          if (x + width > maxW) width = maxW - x;
          if (y + height > maxH) height = maxH - y;
          if (width < MIN_SIZE || height < MIN_SIZE) return;
        }
        onChange({ x, y, width, height });
        setResizeStart({ ...resizeStart, area: { x, y, width, height }, clientX: e.clientX, clientY: e.clientY });
      }
    },
    [mode, drawStart, moveStart, resizeStart, rotateStart, value, clientToLocal, onChange]
  );

  useEffect(() => {
    if (mode !== 'rotating' || !rotateStart) return;
    const onMouseMove = (e: MouseEvent) => {
      const el = overlayRef.current;
      if (!el || !onAngleChangeRef.current) return;
      const rect = el.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      const localY = e.clientY - rect.top;
      const currentDir = Math.atan2(localY - rotateStart.centerY, localX - rotateStart.centerX);
      const deltaDeg = ((currentDir - rotateStart.startDir) * 180) / Math.PI;
      const newAngle = rotateStart.startAngle + deltaDeg;
      onAngleChangeRef.current(Math.round(newAngle));
    };
    const onMouseUp = () => {
      setMode('idle');
      setRotateStart(null);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [mode, rotateStart]);

  const overlayCursor =
    mode === 'drawing'
      ? 'crosshair'
      : mode === 'moving'
        ? 'move'
        : mode === 'rotating'
          ? 'grabbing'
          : mode === 'resizing'
            ? 'nwse-resize'
            : hoverRotation
              ? 'grab'
              : hoverHandle
                ? `${hoverHandle}-resize`
                : hoverInArea
                  ? 'move'
                  : value
                    ? 'crosshair'
                    : 'crosshair';

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (mode === 'drawing' && drawStart && drawCurrent) {
        const x = Math.min(drawStart.x, drawCurrent.x);
        const y = Math.min(drawStart.y, drawCurrent.y);
        const width = Math.max(MIN_SIZE, Math.abs(drawCurrent.x - drawStart.x));
        const height = Math.max(MIN_SIZE, Math.abs(drawCurrent.y - drawStart.y));
        onChange({ x, y, width, height });
        onAngleChange?.(0);
        setDrawStart(null);
        setDrawCurrent(null);
      }
      setMode('idle');
      setMoveStart(null);
      setResizeStart(null);
      setRotateStart(null);
    },
    [mode, drawStart, drawCurrent, onChange, onAngleChange]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverHandle(null);
    setHoverInArea(false);
    setHoverRotation(false);
    if (mode === 'drawing') {
      if (drawStart && drawCurrent) {
        const x = Math.min(drawStart.x, drawCurrent.x);
        const y = Math.min(drawStart.y, drawCurrent.y);
        const width = Math.max(MIN_SIZE, Math.abs(drawCurrent.x - drawStart.x));
        const height = Math.max(MIN_SIZE, Math.abs(drawCurrent.y - drawStart.y));
        onChange({ x, y, width, height });
        onAngleChange?.(0);
      }
      setDrawStart(null);
      setDrawCurrent(null);
    }
    setMode('idle');
    setMoveStart(null);
    setResizeStart(null);
    setRotateStart(null);
  }, [mode, drawStart, drawCurrent, onChange, onAngleChange]);

  const area = value ?? (drawStart && drawCurrent
    ? {
        x: Math.min(drawStart.x, drawCurrent.x),
        y: Math.min(drawStart.y, drawCurrent.y),
        width: Math.max(0, Math.abs(drawCurrent.x - drawStart.x)),
        height: Math.max(0, Math.abs(drawCurrent.y - drawStart.y)),
      }
    : null);

  return (
    <div
      ref={overlayRef}
      className={className}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        cursor: overlayCursor,
        zIndex: 10,
        ...style,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {area && area.width >= MIN_SIZE && area.height >= MIN_SIZE && (
        <>
          {value ? (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                transformOrigin: `${value.x + value.width / 2}px ${value.y + value.height / 2}px`,
                transform: `rotate(${Number(angle) || 0}deg)`,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: value.x,
                  top: value.y,
                  width: value.width,
                  height: value.height,
                  border: '2px dashed rgba(255,255,255,0.8)',
                  boxSizing: 'border-box',
                }}
              />
              {(['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as ResizeHandle[]).map((h) => {
                const r = getHandleRect(value, h);
                return (
                  <div
                    key={h}
                    style={{
                      position: 'absolute',
                      left: r.left,
                      top: r.top,
                      width: r.width,
                      height: r.height,
                      background: '#000',
                      border: '1px solid #fff',
                      cursor: `${h}-resize`,
                    }}
                  />
                );
              })}
              {onAngleChange && (
                <div
                  role="button"
                  tabIndex={0}
                  title="Повернуть (тяните для изменения угла)"
                  style={{
                    position: 'absolute',
                    ...getRotationHandleRect(value),
                    padding: 0,
                    border: '1px solid rgba(255,255,255,0.9)',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    cursor: 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                  </svg>
                </div>
              )}
              <button
                type="button"
                title="Удалить область"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(null);
                  onAngleChange?.(0);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  ...getDeleteHandleRect(value),
                  padding: 0,
                  border: '1px solid rgba(255,255,255,0.9)',
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </div>
          ) : (
            <div
              style={{
                position: 'absolute',
                left: area.x,
                top: area.y,
                width: area.width,
                height: area.height,
                border: '2px dashed rgba(255,255,255,0.8)',
                boxSizing: 'border-box',
                pointerEvents: 'none',
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
